import { Component, inject, signal, effect, OnDestroy } from '@angular/core';
import { SocketService } from '../../services/socket/socket.service';
import { CommonModule } from '@angular/common';

/**
 * Componente de notificação de status da conexão socket
 * Aparece por 3 segundos quando há mudança de status
 */
@Component({
    selector: 'lc-socket-status',
    template: `
        @if (showNotification() && isConnected()) {
            <div
                class="connection-notification"
                [class]="getNotificationClass()"
            >
                <div class="notification-content">
                    <div class="icon">
                        @if (isConnected()) {
                            <svg
                                class="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        } @else {
                            <svg
                                class="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        }
                    </div>
                    <div class="message">
                        <div class="title">{{ getStatusTitle() }}</div>
                        <!-- <div class="subtitle">{{ getStatusMessage() }}</div> -->
                    </div>
                </div>
            </div>
        }
    `,
    styles: [
        `
            .connection-notification {
                position: fixed;
                top: 70px;
                right: 20px;
                z-index: 10000;
                max-width: 320px;
                border-radius: 12px;
                box-shadow:
                    0 10px 25px -5px rgba(0, 0, 0, 0.1),
                    0 10px 10px -5px rgba(0, 0, 0, 0.04);
                animation: slideInRight 0.3s ease-out;
            }

            .connection-notification.connected {
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                color: white;
            }

            .connection-notification.disconnected {
                background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                color: white;
            }

            .connection-notification.connecting {
                background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
                color: white;
            }

            .notification-content {
                display: flex;
                align-items: center;
                padding: 16px 20px;
                gap: 12px;
            }

            .icon {
                flex-shrink: 0;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .message {
                flex: 1;
            }

            .title {
                font-weight: 600;
                font-size: 14px;
                line-height: 1.25;
                margin-bottom: 2px;
            }

            .subtitle {
                font-size: 12px;
                opacity: 0.9;
                line-height: 1.25;
            }

            @keyframes slideInRight {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }

            @keyframes slideOutRight {
                from {
                    transform: translateX(0);
                    opacity: 1;
                }
                to {
                    transform: translateX(100%);
                    opacity: 0;
                }
            }

            .connection-notification.hiding {
                animation: slideOutRight 0.3s ease-in forwards;
            }

            /* Dark mode */
            .dark .connection-notification {
                box-shadow:
                    0 10px 25px -5px rgba(0, 0, 0, 0.3),
                    0 10px 10px -5px rgba(0, 0, 0, 0.1);
            }
        `,
    ],
    standalone: true,
    imports: [CommonModule],
})
export class SocketStatusComponent implements OnDestroy {
    readonly socketService = inject(SocketService);

    // Signals para controle de estado
    readonly showNotification = signal(false);
    readonly isConnected = signal(false);
    readonly isConnecting = signal(false);

    private hideTimeout?: number;
    private lastConnectionState?: boolean;

    constructor() {
        // Effect para monitorar mudanças de status
        effect(
            () => {
                const connected = this.socketService.isConnected();
                const connecting = this.socketService.isConnecting();

                // Só mostra notificação se houve mudança de estado
                if (this.lastConnectionState !== connected) {
                    this.showConnectionNotification(connected, connecting);
                    this.lastConnectionState = connected;
                }
            },
            { allowSignalWrites: true }
        );
    }

    ngOnDestroy(): void {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }
    }

    private showConnectionNotification(connected: boolean, connecting: boolean): void {
        // Cancela timeout anterior se existir
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }

        // Atualiza os signals
        this.isConnected.set(connected);
        this.isConnecting.set(connecting);

        // Mostra a notificação
        this.showNotification.set(true);

        // Esconde após 3 segundos
        this.hideTimeout = window.setTimeout(() => {
            this.showNotification.set(false);
        }, 1000);
    }

    getNotificationClass(): string {
        if (this.isConnected()) {
            return 'connected';
        } else if (this.isConnecting()) {
            return 'connecting';
        } else {
            return 'disconnected';
        }
    }

    getStatusTitle(): string {
        if (this.isConnected()) {
            return 'Conectado';
        } else if (this.isConnecting()) {
            return 'Conectando...';
        } else {
            return 'Desconectado';
        }
    }

    getStatusMessage(): string {
        if (this.isConnected()) {
            return 'Conexão com o servidor estabelecida';
        } else if (this.isConnecting()) {
            return 'Tentando conectar ao servidor...';
        } else {
            return 'Sem conexão com o servidor';
        }
    }
}
